/**
 * Strength rules for a password being set — sign-up and change-password.
 *
 * Mirrors PASSWORD_RULES in identity-service/dto-schema/user/auth.js, which is
 * what actually enforces them; this copy only drives the inline checklist and
 * stops a doomed submit early. Keep the two lists in step.
 */

export const PASSWORD_MIN_CHARS = 8;

/** bcrypt reads only the first 72 bytes, so identity-service refuses more. */
export const PASSWORD_MAX_BYTES = 72;

export interface PasswordRule {
  id: string;
  label: string;
  test: (value: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  { id: 'length', label: `At least ${PASSWORD_MIN_CHARS} characters`, test: (v) => v.length >= PASSWORD_MIN_CHARS },
  { id: 'lowercase', label: 'A lowercase letter', test: (v) => /[a-z]/.test(v) },
  { id: 'uppercase', label: 'An uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { id: 'number', label: 'A number', test: (v) => /[0-9]/.test(v) },
  { id: 'symbol', label: 'A symbol', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

/** UTF-8 byte length — an emoji counts as 4, matching the server's check. */
export function passwordBytes(value: string): number {
  return new TextEncoder().encode(value).length;
}

/** True when the password passes every rule and fits bcrypt's 72-byte limit. */
export function isStrongPassword(value: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(value)) && passwordBytes(value) <= PASSWORD_MAX_BYTES;
}
